import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { type ButtonSize } from '#app/components/Buttons/types.js';
import { AddToListMenu } from '#app/components/Network/AddToListMenu/AddToListMenu.js';
import { useAddToListMenu } from '#app/components/Network/AddToListMenu/useAddToListMenu.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { CreateContactForUserProfileSource } from '@contra/graphql-types';
import {
  BookmarkOutlineIcon,
  BookmarkSolidIcon,
  UserAddOutlineIcon,
  UserCheckSolidIcon,
} from '@contra/icons/icons';
import { Suspense, useCallback } from 'react';

type SaveUserButtonProps = SaveUserButtonV1Props & SaveUserButtonV2Props;

type SaveUserButtonV1Props = {
  readonly addContact: () => void;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly removeContact: () => void;
  readonly size?: ButtonSize;
  readonly userName: null | string;
};

type SaveUserButtonV2Props = {
  readonly analyticsSection: string;
  readonly contactId: null | string;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly size?: ButtonSize;
  readonly source: CreateContactForUserProfileSource;
  readonly userProfile: {
    firstName: string;
    id: string;
    isSavedContact: boolean;
  };
};

const SaveUserButtonV1 = ({
  addContact,
  hideBorder,
  isSavedContact,
  removeContact,
  size,
  userName,
}: SaveUserButtonV1Props) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const { handleOnboardingRedirectionModalOpen, shouldRedirectUser } =
    useOnboardingRedirection({
      overrideUserType: 'client',
      redirectTo: location.path,
    });

  const onSaveClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (shouldRedirectUser) {
        handleOnboardingRedirectionModalOpen();
      } else {
        addContact();
      }
    },
    [addContact, handleOnboardingRedirectionModalOpen, shouldRedirectUser],
  );

  const onRemoveClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (shouldRedirectUser) {
        handleOnboardingRedirectionModalOpen();
      } else {
        removeContact();
      }
    },
    [handleOnboardingRedirectionModalOpen, removeContact, shouldRedirectUser],
  );

  const button = isSavedContact ? (
    <CircularIconButton
      aria-label={`${userName ?? 'This user'} is in your network`}
      icon={UserCheckSolidIcon}
      onClick={onRemoveClick}
      size={(size ?? isMobile) ? 'large' : 'small'}
      variant={hideBorder ? 'V2Link' : 'V2TertiaryOutline'}
    />
  ) : (
    <CircularIconButton
      aria-label={`Add ${userName ?? 'this user'} to your network`}
      icon={UserAddOutlineIcon}
      onClick={onSaveClick}
      size={(size ?? isMobile) ? 'large' : 'small'}
      variant={hideBorder ? 'V2Link' : 'V2TertiaryOutline'}
    />
  );

  if (isSavedContact) {
    return button;
  }

  return (
    <Tooltip
      content={`Add ${userName ?? 'this user'} to network. They will not be notified.`}
      width="auto"
    >
      {button}
    </Tooltip>
  );
};

const SaveButtonV2 = ({
  firstName,
  hideBorder,
  isSavedContact,
  onClick,
  size,
}: {
  readonly firstName: string;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly onClick: () => void;
  readonly size?: ButtonSize;
}) => {
  const isMobile = useIsMobile();

  return (
    <CircularIconButton
      aria-label={
        isSavedContact ? `${firstName} is saved` : `Save ${firstName}`
      }
      icon={isSavedContact ? BookmarkSolidIcon : BookmarkOutlineIcon}
      onClick={onClick}
      size={size ?? (isMobile ? 'large' : 'small')}
      variant={
        hideBorder
          ? isSavedContact
            ? 'V2BrandPurpleLink'
            : 'V2Link'
          : isSavedContact
            ? 'V2BrandPurple'
            : 'V2TertiaryOutline'
      }
    />
  );
};

const SaveUserButtonV2 = ({
  analyticsSection,
  contactId,
  hideBorder,
  isSavedContact,
  size,
  source,
  userProfile,
}: SaveUserButtonV2Props) => {
  const addToListMenuProps = useAddToListMenu({
    analyticsSection,
    isSaved: isSavedContact,
  });

  const trigger = (
    <SaveButtonV2
      firstName={userProfile.firstName}
      hideBorder={hideBorder}
      isSavedContact={isSavedContact}
      onClick={() => {
        if (isSavedContact) {
          addToListMenuProps.setIsActionsMenuOpen(true);
        } else {
          addToListMenuProps.handleOpenListMenu();
        }
      }}
      size={size}
    />
  );

  if (
    addToListMenuProps.isActionsMenuOpen ||
    addToListMenuProps.isListMenuOpen ||
    isSavedContact
  ) {
    return (
      <Suspense fallback={trigger}>
        <AddToListMenu
          addToListMenuProps={addToListMenuProps}
          analyticsSection="test"
          contactId={contactId}
          source={source}
          trigger={trigger}
          userProfile={userProfile}
        />
      </Suspense>
    );
  }

  return trigger;
};

export const SaveUserButton = (args: SaveUserButtonProps) => {
  const reworkedSaveToListsFlow = useFeatureFlag('reworkedSaveToListsFlow');

  if (reworkedSaveToListsFlow) {
    return <SaveUserButtonV2 {...args} />;
  }

  return <SaveUserButtonV1 {...args} />;
};
